import React from "react";
import { LuClipboardEdit } from "react-icons/lu";
import { ChevronDownIcon } from "./ChevronDownIcon";
import StartDateTime from "./StartDateTime";
import DuplicateIndicator from "./DuplicateIndicator";
import type { DuplicateOrder, Entity, CellResult, RenderGroupCellProps } from "./types";

export function getGroupCellData({
  group,
  columnKey,
  columns,
  entities,
  isExpanded,
  onToggle,
  t,
}: RenderGroupCellProps): CellResult {
  const totalOrders = group.orders.length;
  const pendingOrders = group.orders.filter(
    (order) => order.status === "pending_review" || order.status === "active"
  ).length;
  const allValidated = pendingOrders === 0;
  const firstOrder = group.orders[0];

  const columnDef = columns.find((col) => col.uid === columnKey);
  const columnStyle = columnDef?.style || {};

  // Get unique entities in the group
  const uniqueEntityIds = [...new Set(group.orders.map((order) => order.entity_id))];
  const uniqueEntities = uniqueEntityIds
    .map((entityId) => entities.find((entity) => entity.entity_id === entityId))
    .filter(Boolean) as Entity[];

  const getGroupStatusContent = () => {
    const baseClasses =
      "rounded-full text-center text-small h-7 items-center leading-7 flex justify-center font-bold w-32";
    const gradientClasses = allValidated
      ? "bg-gradient-to-r from-vistactagreenlight to-vistactagreen"
      : "bg-gradient-to-r from-vistactaorangelight to-vistactaorange";

    return (
      <div className="relative">
        {/* Layer 2 - back */}
        <div
          className={`${baseClasses} ${gradientClasses} absolute opacity-30`}
          style={{ transform: "translateX(8px) translateY(6px)" }}
        />
        {/* Layer 1 - middle */}
        <div
          className={`${baseClasses} ${gradientClasses} absolute opacity-50`}
          style={{ transform: "translateX(4px) translateY(3px)" }}
        />
        {/* Main layer */}
        <div className={`${baseClasses} ${gradientClasses} text-white relative z-10`}>
          {allValidated ? (
            <>
              <span className="text-small">{t("validated")} |</span>
              <LuClipboardEdit className="ml-2" />
            </>
          ) : (
            <>
              <span className="text-xs">
                {t("to_process")} | {pendingOrders}/{totalOrders}
              </span>
              <LuClipboardEdit className="ml-2" />
            </>
          )}
        </div>
      </div>
    );
  };

  let content: React.ReactNode = null;

  switch (columnKey) {
    case "entity_name":
      content = (
        <>
          {uniqueEntities.length === 1 && uniqueEntities[0] && (
            <div className="flex items-center">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: uniqueEntities[0].entity_color }}
              >
                {uniqueEntities[0].entity_initials}
              </div>
            </div>
          )}
          {uniqueEntities.length > 1 && (
            <div className="flex items-center">
              <div className="flex -space-x-5">
                {uniqueEntities.map((entity, index) => (
                  <div
                    key={entity.entity_id}
                    className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold"
                    style={{
                      backgroundColor: entity.entity_color,
                      zIndex: uniqueEntities.length - index,
                    }}
                  >
                    {entity.entity_initials}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      );
      break;

    case "status": {
      const childJobIds = new Set(group.orders.map((order) => order.jobID));
      const externalDuplicatesMap = new Map<string, DuplicateOrder>();
      for (const order of group.orders) {
        for (const dup of order.duplicate_job_ids ?? []) {
          if (!childJobIds.has(dup.jobID)) {
            externalDuplicatesMap.set(dup.jobID, dup);
          }
        }
      }
      const externalDuplicates = Array.from(externalDuplicatesMap.values());

      content = (
        <div className="flex items-center gap-2">
          {getGroupStatusContent()}
          {externalDuplicates.length > 0 && (
            <DuplicateIndicator duplicates={externalDuplicates} t={t} />
          )}
        </div>
      );
      break;
    }

    case "order_delivery_date":
      content = <StartDateTime start_datetime={firstOrder.order_delivery_date} />;
      break;

    case "start_datetime":
      content = <StartDateTime start_datetime={firstOrder.start_datetime} />;
      break;

    case "subject":
      content = (
        <div className="flex items-center gap-3">
          <span className="font-medium text-default-600">{firstOrder.subject}</span>
        </div>
      );
      break;

    case "actions":
      content = (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="p-2 hover:bg-default-100 rounded-full transition-transform"
          aria-label={isExpanded ? t("collapse") : t("expand")}
        >
          <ChevronDownIcon
            className={`text-default-500 transition-transform duration-200 ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>
      );
      break;

    case "customer_name":
    case "assignment":
    case "warnings":
    case "products_counter":
    case "order_id":
    case "deadline":
    default:
      break;
  }

  return { content, style: columnStyle };
}

export default getGroupCellData;
